import {html, LitElement, type PropertyValues, type TemplateResult} from "lit";
import {customElement, property, query, state} from "lit/decorators.js";
import Styles from "./jobListPreview.lit.scss";
import type {Job} from "./jobService";
import {JobPreviewRequest, JobService} from "./jobService";
import {schedule} from "../../common/utils/promises";
import {EopImageSpinner, EopOverlaySpinner} from "../../page/elements/spinner";
import {resolve} from "../../container";
import "../../page/elements/input/dropdown";
import {Dictionary} from "../../page/elements/dictionary";
import type {Nullable} from "../../bootstrap/common/types";
import {DROPDOWN_EVENT, type DropdownEvent} from "../../page/elements/input/inputEvents";
import type {DataSelectOption, EopDropdown} from "../../page/elements/input/dropdown";
import {trackingButtonInteract, trackingTextlinkInteract} from "../../tracking/trackingAttributes";

type TitleFilter = {
    label: string;
    value: string;
    preset: boolean;
};


@customElement("eop-job-list-preview")
export class EopJobListPreview extends LitElement {

    public static readonly styles = Styles;

    @property({attribute: "limit", type: Number})
    private limit: number;
    @property({attribute: "filter-job-types"})
    private filterJobTypes: string;
    @property({attribute: "filter-job-locations"})
    private filterJobLocations: string;
    @property({attribute: "show-job-locations-dropdown"})
    private showJobLocationsDropdown: boolean;
    @property({attribute: "filter-job-companies"})
    private filterJobCompanies: string;
    @property({attribute: "filter-job-titles"})
    private filterJobTitles: string;
    @property({attribute: "show-job-titles-dropdown"})
    private showJobTitlesDropdown: boolean;

    @state()
    private jobs: Job[] = [];
    @state()
    private offset: number = 0;
    @state()
    private total: Nullable<number> = null;

    @query("eop-image-spinner")
    protected fetchSpinner: EopImageSpinner;
    @query("eop-overlay-spinner")
    protected fetchMoreSpinner: EopOverlaySpinner;

    private jobLocations: string[];
    private jobTitles: TitleFilter[];
    private currentLocationFilter: Nullable<string[]> = null;
    private currentTitleFilter: Nullable<string[]> = null;
    private dictionary: Dictionary;

    public constructor(private jobService: JobService = resolve(JobService)) {
        super();
    }

    public connectedCallback(): void {
        super.connectedCallback();

        this.dictionary = Dictionary.of(this);

        this.jobLocations = this.filterJobLocations?.parseCommaSeparated() ?? [];
        this.jobTitles = this.filterJobTitles
            ?.parseAsJSON<TitleFilter[]>();
    }

    public render(): TemplateResult {
        return html`
            ${this.renderIntroText()}
            ${this.renderDropdowns()}
            <eop-overlay-spinner></eop-overlay-spinner>
            <eop-image-spinner></eop-image-spinner>
            ${this.renderResultsArea()}
            ${this.renderLoadMoreButton()}
        `;
    }

    private renderIntroText(): Nullable<TemplateResult> {
        if (this.total === 0) {
            return null;
        }
        return html`
            <slot name="intro-text"></slot>
        `;
    }

    private renderDropdowns(): Nullable<TemplateResult> {
        let locationDropdown = null;
        let titleDropdown = null;
        const placeholder = this.dictionary.translate("JOB_PREVIEW_DROPDOWN_PLACEHOLDER");

        if (this.showJobLocationsDropdown && this.jobLocations.length > 1) {
            locationDropdown = this.renderDropdown("job-locations", this.dictionary.translate("JOB_PREVIEW_LOCATION_DROPDOWN"), placeholder,
                this.jobLocations.map(location => ({
                    label: location,
                    value: location
                } as DataSelectOption))
            );
        }

        if (this.showJobTitlesDropdown && this.jobTitles.length > 1) {
            titleDropdown = this.renderDropdown("job-titles", this.dictionary.translate("JOB_PREVIEW_TITLE_DROPDOWN"), placeholder,
                this.jobTitles.map(title => ({
                    label: title.label,
                    value: title.value
                } as DataSelectOption))
            );
        }
        if (locationDropdown !== null || titleDropdown !== null) {
            return html`
                <div class="filter-dropdowns">
                    ${locationDropdown}
                    ${titleDropdown}
                </div>`;
        }
        return null;
    }

    private renderDropdown(id: string, label: string, placeholder: string = "", options: DataSelectOption[] = []): Nullable<TemplateResult> {
        return html`
            <div class="dropdown-container">
                <eop-dropdown id=${id} .inputId=${id} .label=${label} .placeholder=${placeholder} .options=${options}></eop-dropdown>
            </div>
        `;
    }

    private renderResultsArea(): Nullable<TemplateResult> {
        if (this.total === 0) {
            return html`
                <div class="empty-results">
                    <slot name="empty-results-text"></slot>
                </div>
            `;
        }

        if (this.jobs.isEmpty()) {
            return null;
        }

        return html`
            <div class="items">${(this.renderEntries())}</div>`;
    }

    private renderEntries(): TemplateResult[] {
        return this.jobs.map(entry => this.renderEntry((entry)));
    }

    private renderEntry(job: Job): TemplateResult {
        return html`
            <div class="item">
                <a class="linked-job-title"
                   href=${job.url}
                   target="_blank"
                   rel="noopener"
                   ${trackingTextlinkInteract(job.title, job.url)}
                >${job.title}</a>
                <div class="location">${job.company} in ${job.locations.join(", ")}</div>
            </div>
        `;
    }

    private renderLoadMoreButton(): Nullable<TemplateResult> {
        const fetchMoreLabel = this.dictionary.translate("JOB_PREVIEW_FETCH_MORE");
        if (this.jobs.length < (this.total ?? 0)) {
            return html`
                <button class="load-more-button" @click=${this.fetchMoreHandler} ${trackingButtonInteract(fetchMoreLabel)}>
                    ${fetchMoreLabel}
                </button>
            `;
        }
        return null;
    }

    protected firstUpdated(_changedProperties: PropertyValues): void {
        super.firstUpdated(_changedProperties);

        const locationDropdown = this.shadowRoot?.querySelector<EopDropdown>("#job-locations");
        locationDropdown?.addEventListener(DROPDOWN_EVENT, (event: Event) => {
            const eventDetails = (event as DropdownEvent).detail;
            const value = eventDetails.value;
            if (value === "") {
                this.currentLocationFilter = null;
            } else {
                this.currentLocationFilter = [value];
            }
            this.offset = 0;
            void schedule(this.fetchMoreSpinner.spinWhile(this.fetch()))
                .as("filter by location");
        });

        const titleDropdown = this.shadowRoot?.querySelector<EopDropdown>("#job-titles");
        titleDropdown?.addEventListener(DROPDOWN_EVENT, (event: Event) => {
            const eventDetails = (event as DropdownEvent).detail;
            const value = eventDetails.value;
            if (value === "") {
                this.currentTitleFilter = null;
            } else {
                this.currentTitleFilter = value.parseCommaSeparated();
            }
            this.offset = 0;
            void schedule(this.fetchMoreSpinner.spinWhile(this.fetch()))
                .as("filter by title");
        });

        void schedule(this.fetchSpinner.spinWhile(this.fetch()))
            .as("fetch");
    }

    private fetchMoreHandler(): void {
        this.offset += this.limit;
        void schedule(this.fetchMoreSpinner.spinWhile(this.fetch(true)))
            .as("more");
    }

    private async fetch(loadMore: boolean = false): Promise<void> {
        const response = await this.jobService.jobPreview(
            new JobPreviewRequest(
                this.filterJobTypes?.parseCommaSeparated() ?? [],
                this.currentLocationFilter ?? this.jobLocations,
                this.filterJobCompanies?.parseCommaSeparated() ?? [],
                this.currentTitleFilter ?? this.jobTitles.flatMap(filter => filter.value.parseCommaSeparated()),
                this.limit,
                this.offset
            )
        );
        if (loadMore) {
            this.jobs = this.jobs.concat(response.jobs);
        } else {
            this.jobs = response.jobs;
        }
        this.total = response.total;
    }
}